import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { Header } from '../../../../../components/atoms/header/header';
import { useAllIndicators } from '../../../context/indicators';
import { useAnswersForRespondentType, useSurveyForRespondentType } from '../../../context/render-context';
import { SurveySet } from '../../../survey-types';
import * as createBlockUtils from '../../../utils/create-block';
import { AnsweredStatus, useConditions } from '../../../utils/surveys/conditions';
import { getCalculationId, getExpressionRenderedText } from '../../../utils/surveys/expressions';
import { WithIndicator } from '../indicators/with-indicator';
import { useExpression } from '../measure-breakdown/hooks/use-expression';
import { ExpressionCalculationContent } from '../show-calculation/show-calculation';

import { useCalculationExpressionNames } from './hooks/use-calculation-expressions';

type CalculationAppendixProps = {
  readonly isPdf?: boolean;
};

const CalculationAppendix = ({ isPdf = false }: CalculationAppendixProps) => {
  const { t } = useTranslation();
  const expressions = useCalculationExpressionNames();
  const allIndicators = useAllIndicators();

  if (!isPdf || !expressions.length) return null;

  const multipleRespondents = allIndicators.length > 1;

  return (
    <div className="mt-8 pt-6 border-t-2 border-gray-300" style={{ pageBreakBefore: 'always', breakBefore: 'page' }}>
      <Header className="mb-6" level="h1">
        {t('report.blocks.measureBreakdown.appendixTitle')}
      </Header>

      {expressions.map(({ expressionName, respondentType }) => {
        return (
          <ShowCalculation
            expressionName={expressionName}
            key={`${expressionName}-${respondentType}`}
            multipleRespondents={multipleRespondents}
            respondentType={respondentType}
            t={t}
          />
        );
      })}
    </div>
  );
};

type ShowCalculationProps = {
  readonly expressionName: string;
  readonly respondentType: string;
  readonly multipleRespondents?: boolean;
  readonly t: TFunction;
};

const ShowCalculation = ({ respondentType, expressionName, multipleRespondents = false, t }: ShowCalculationProps) => {
  const survey = useSurveyForRespondentType(respondentType);
  const answers = useAnswersForRespondentType(respondentType);
  const expression = useExpression({ survey, selectedExpressionName: expressionName });
  const { answeredStatus } = useConditions(expression, answers);

  if (!expression || !survey || !answers || answeredStatus !== AnsweredStatus.COMPLETED) return null;

  const renderedText = getExpressionRenderedText(survey, expression);

  if (!renderedText) return null;

  return (
    <div className="mb-6 pb-4 border-b border-gray-200 last:border-b-0">
      <WithIndicator displayIndicator={multipleRespondents} respondentType={respondentType}>
        <Header id={`calculation-${getCalculationId(expression, respondentType)}`} level="h2">
          {`${expression.measureName ?? expression.conditionName} ${t('ui-components.showCalculation.calculation')}`}
        </Header>
      </WithIndicator>
      <ExpressionCalculationContent isPdf renderedText={renderedText} />
    </div>
  );
};

const createCalculationAppendixBlock = ({ isPdf, surveySet }: { isPdf?: boolean; surveySet: SurveySet }) => {
  return createBlockUtils.makeBlockFactory(
    { overrides: { selectedRespondentType: surveySet[0]?.respondentType } },
    {
      label: 'Calculation Appendix',
      render: () => <CalculationAppendix isPdf={isPdf} />,
    }
  )({ surveySet });
};

export { CalculationAppendix, createCalculationAppendixBlock };
